import { CalendarOutlined } from '@ant-design/icons';
import { Divider } from 'antd';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { Fragment } from 'react';

import styles from './InfoBlock.module.scss';

export type InfoRow = {
  label: string;
  value?: ReactNode;
  bold?: boolean;
  subRows?: InfoRow[];
  variant?: 'default' | 'subheader' | 'date' | 'subrow';
  isTitle?: boolean;
  isValueCentered?: boolean;
};

export type InfoCategory = {
  label?: string;
  rows: InfoRow[];
};

export type InfoSection = {
  title?: string;
  rows?: InfoRow[];
  categories?: InfoCategory[];
  info?: ReactNode;
  infoPosition?: 'before' | 'after';
};

type Props = {
  title?: string;
  actions?: ReactNode;
  withBorder?: boolean;
} & (
  | { rows: InfoRow[]; sections?: never; children?: never }
  | { sections: InfoSection[]; rows?: never; children?: never }
  | { children: ReactNode; rows?: never; sections?: never }
);

export const filterRows = (rows: InfoRow[]) =>
  rows.filter(
    ({ value, variant }) =>
      variant === 'subheader' ||
      variant === 'subrow' ||
      variant === 'date' ||
      (value !== undefined && value !== null && value !== '')
  );

const groupSubRowsByNightCount = (subRows: InfoRow[]): InfoRow[][] =>
  subRows
    .reduce<InfoRow[][]>((acc, sub) => {
      if (acc.length === 0) {
        acc.push([]);
      }

      acc[acc.length - 1].push(sub);

      if (sub.variant === 'subheader') {
        acc.push([]);
      }

      return acc;
    }, [])
    .filter((group) => group.length > 0);

// ---------------------------------------------------------------------------
// SubRow-level components
// ---------------------------------------------------------------------------

const DateSubRow = ({ label, index }: { label: string; index: number }) => (
  <div key={`${label}-${index}`} className={styles.dateRow}>
    <CalendarOutlined />
    <span>{label}</span>
  </div>
);

const PriceSubRow = ({
  label,
  value,
  index,
}: {
  label: string;
  value?: ReactNode;
  index: number;
}) => (
  <div key={`${label}-${index}`} className={styles.subRow}>
    <div className={styles.label}>{label}</div>
    <div className={styles.value}>{value}</div>
  </div>
);

const NightCountRow = ({ label }: { label: string }) => (
  <div className={styles.nightCountRow}>
    <CalendarOutlined />
    {label}
  </div>
);

// ---------------------------------------------------------------------------
// SubRowGroup — one price group (adult rows + child rows + night count)
// ---------------------------------------------------------------------------

const SubRowGroup = ({
  group,
  groupIndex,
}: {
  group: InfoRow[];
  groupIndex: number;
}) => {
  const nightCount = group.find((s) => s.variant === 'subheader');
  const rows = group.filter((s) => s.variant !== 'subheader');

  return (
    <div key={groupIndex} className={styles.subRowGroup}>
      {rows.map((sub, index) =>
        sub.variant === 'date' ? (
          <DateSubRow
            key={`${sub.label}-${index}`}
            label={sub.label}
            index={index}
          />
        ) : (
          <PriceSubRow
            key={`${sub.label}-${index}`}
            label={sub.label}
            value={sub.value}
            index={index}
          />
        )
      )}
      {nightCount && <NightCountRow label={nightCount.label} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SubRowList — renders all groups inside a parent row
// ---------------------------------------------------------------------------

const SubRowList = ({ subRows }: { subRows: InfoRow[] }) => {
  const groups = groupSubRowsByNightCount(subRows);

  return (
    <div className={styles.subRows}>
      {groups.map((group, groupIndex) => (
        <SubRowGroup key={groupIndex} group={group} groupIndex={groupIndex} />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Row — single row with optional sub-rows
// ---------------------------------------------------------------------------

const Row = ({
  label,
  value,
  bold = true,
  subRows,
  isTitle = false,
  isValueCentered = false,
}: InfoRow) => (
  <div className={styles.rowGroup}>
    <div
      className={classNames(styles.row, {
        [styles.rowBold]: bold,
        [styles.rowTitle]: isTitle,
      })}
    >
      <div className={styles.label}>{label}</div>
      <div
        className={classNames(styles.value, {
          [styles.centered]: isValueCentered,
        })}
      >
        {value}
      </div>
    </div>
    {subRows && subRows.length > 0 && <SubRowList subRows={subRows} />}
  </div>
);

// ---------------------------------------------------------------------------
// RowList — renders a flat list of rows, handling special variants
// ---------------------------------------------------------------------------

export const RowList = ({ rows }: { rows: InfoRow[] }) => (
  <>
    {filterRows(rows).map((row) => {
      const { label, variant } = row;

      if (variant === 'subheader' || variant === 'subrow') {
        return (
          <div key={label} className={styles[`${variant}Row`]}>
            {label}
          </div>
        );
      }

      return <Row key={label} {...row} />;
    })}
  </>
);

// ---------------------------------------------------------------------------
// InfoBlockRows — standalone rows block (used outside sections)
// ---------------------------------------------------------------------------

export const InfoBlockRows = ({ rows }: { rows: InfoRow[] }) => {
  const filtered = filterRows(rows);

  if (filtered.length === 0) {
    return null;
  }

  return (
    <div className={styles.list}>
      <RowList rows={filtered} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// CategoryList — renders a list of categories within a section
// ---------------------------------------------------------------------------

const CategoryList = ({ categories }: { categories: InfoCategory[] }) => (
  <div className={styles.categories}>
    {categories
      .filter((c) => filterRows(c.rows).length > 0)
      .map((category) => (
        <div key={category.label} className={styles.category}>
          {category.label && (
            <div className={styles.categoryLabel}>{category.label}</div>
          )}
          <RowList rows={category.rows} />
        </div>
      ))}
  </div>
);

// ---------------------------------------------------------------------------
// SectionBody — rows or categories inside a section
// ---------------------------------------------------------------------------

const SectionBody = ({
  section,
}: {
  section: Pick<InfoSection, 'title' | 'rows' | 'categories'>;
}) => (
  <div className={styles.sectionList}>
    {section.title && (
      <div className={styles.sectionTitle}>{section.title}</div>
    )}
    {section.categories ? (
      <CategoryList categories={section.categories} />
    ) : (
      section.rows && <RowList rows={section.rows} />
    )}
  </div>
);

// ---------------------------------------------------------------------------
// SectionItem — one section with optional info alert before/after
// ---------------------------------------------------------------------------

const SectionItem = ({
  section,
  index,
}: {
  section: InfoSection;
  index: number;
}) => {
  const showBefore = section.info && section.infoPosition !== 'after';
  const showAfter = section.info && section.infoPosition === 'after';

  return (
    <div key={section.title ?? index}>
      {index > 0 && <Divider className={styles.divider} />}
      {showBefore && (
        <div className={classNames(styles.info, styles.isBefore)}>
          {section.info}
        </div>
      )}
      <SectionBody section={section} />
      {showAfter && (
        <div className={classNames(styles.info, styles.isAfter)}>
          {section.info}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// InfoBlockSections — exported helper for sections-only usage
// ---------------------------------------------------------------------------

export const InfoBlockSections = ({
  sections,
}: {
  sections: InfoSection[];
}) => {
  const filtered = sections.filter(
    (s) =>
      (s.rows && filterRows(s.rows).length > 0) ||
      (s.categories && s.categories.some((c) => filterRows(c.rows).length > 0))
  );

  if (filtered.length === 0) {
    return null;
  }

  return (
    <>
      {filtered.map((section, index) => (
        <Fragment key={section.title ?? index}>
          {index > 0 && <Divider className={styles.divider} />}
          <div className={styles.list}>
            {section.title && (
              <div className={styles.sectionTitle}>{section.title}</div>
            )}
            {section.categories ? (
              <CategoryList categories={section.categories} />
            ) : (
              section.rows && <RowList rows={section.rows} />
            )}
          </div>
        </Fragment>
      ))}
    </>
  );
};

// ---------------------------------------------------------------------------
// InfoBlock — root component
// ---------------------------------------------------------------------------

const hasVisibleContent = (
  props: Pick<Props, 'rows' | 'sections'> & { children?: ReactNode }
) => {
  if (props.children !== undefined) {
    return true;
  }

  if (props.rows) {
    return filterRows(props.rows).length > 0;
  }

  return (props.sections ?? []).some(
    (s) =>
      (s.rows && filterRows(s.rows).length > 0) ||
      (s.categories && s.categories.some((c) => filterRows(c.rows).length > 0))
  );
};

export const InfoBlock = ({
  title,
  actions,
  rows,
  sections,
  children,
  withBorder = true,
}: Props) => {
  if (!hasVisibleContent({ rows, sections, children })) {
    return null;
  }

  return (
    <section
      className={classNames(styles.infoBlock, {
        [styles.withBorder]: withBorder,
      })}
    >
      {title && (
        <div className={styles.header}>
          <span className={styles.headerTitle}>{title}</span>
          {actions && <div className={styles.headerActions}>{actions}</div>}
        </div>
      )}
      <div className={styles.body}>
        {children !== undefined ? (
          children
        ) : rows ? (
          <div className={styles.list}>
            <RowList rows={rows} />
          </div>
        ) : (
          (sections ?? []).map((section, index) => (
            <SectionItem
              key={section.title ?? index}
              section={section}
              index={index}
            />
          ))
        )}
      </div>
    </section>
  );
};
